import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { Language, PaymentState, ReceiptType } from "@/data/model/Enums";
import type { ITransaction, IVatGroupDto } from "@/data/model/Order";



import { BaseModal } from "@/shared/elements/BaseModal";
import { PaymentMethodUtil } from "@/shared/utils/PaymentMethodUtil";
import { toMoney } from "@/shared/utils/format";



import { trans } from "@/operator/localization/Trans";



import { i18n } from "@/portal/Trans";
import { PaymentInfoHeader } from "@/portal/elements/transaction/PaymentInfoHeader";
import { PaymentItemsList } from "@/portal/elements/transaction/PaymentItemList";
import { PrintReceiptWindow } from "@/portal/modal/PrintReceiptWindow";
import { SendOrderReceiptWindow } from "@/portal/modal/SendOrderReceiptWindow";


export class TransactionDetailsWindow extends BaseModal {
    private $head!: JQuery;
    private $itemList!: JQuery;
    private $totals!: JQuery;
    private $receiptDropDown?: JQuery;

    private readonly orderDao: OrderDao;
    private readonly transactionId: string;

    private isInitialized?: boolean;

    constructor(transactionId: string) {
        super(undefined, undefined, false, true);

        this.transactionId = transactionId;

        this.orderDao = Factory.getInstance<OrderDao>(OrderDao);

        this.$element.addClass("transaction-details");

        this.loadTransaction(transactionId);
    }

    private async loadTransaction(id: string) {
        const transaction = await this.orderDao.getTransaction(id);
        this.updateModalContent(transaction);
    }

    private updateModalContent(transaction: ITransaction) {
        const tip = transaction.tip ?? 0;

        // if it's the first time - initialize containers
        if (!this.isInitialized) {
            this.$body.addClass("scrollable");
            this.$head = $('<div class="head" />').appendTo(this.$header);
            this.$itemList = $('<div class="itemlist" />').appendTo(this.$body);
            this.$totals = $('<div class="totals" />').appendTo(this.$footer!);
            this.isInitialized = true;
        }

        if (this.$receiptDropDown != null) {
            this.$receiptDropDown.remove();
            this.$receiptDropDown = undefined;
        }

        // cleanup previous data
        this.$head.empty();
        this.$itemList.empty();
        this.$totals.empty();

        // set header info
        PaymentInfoHeader.render(this.$head, transaction);

        // set payment items list
        PaymentItemsList.render(this.$itemList, transaction.paymentItems, true);

        // display discount
        if (transaction.orderLevelDiscountAmount > 0) {
            const $discount = $(
                `<div class="totals-item"><div> ${i18n("order.fields.discount")} </div><div style="color: #fd397a">${toMoney(-1 * transaction.orderLevelDiscountAmount, true)}</div></div>`
            );
            this.$totals.append($discount);
        }

        // set vat groups in summary
        for (const vatGroup of transaction.vatGroups) {
            this.$totals.append(
                `<div class="totals-item"><div>${this.getVatGroupTitle(vatGroup)}</div><div>${toMoney(vatGroup.totalAmount, true)}</div></div>`
            );
        }

        // display tip
        if (tip > 0) {
            const $tip = $(
                `<div class="totals-item"><div> ${i18n("order.fields.tip")} </div><div>${tip.toFixed(2)}</div></div>`
            );
            if (transaction.cancelledOrdersTip > 0) {
                $tip.addClass("cancelled");
            }
            this.$totals.append($tip);
        }

        const paymentMethodDivs = PaymentMethodUtil.generatePaymentMethodSplitDivs("", transaction.paymentMethodSplits);

        // display summary
        $(
            `<div class='total totals-item'>
                <div style="display: flex;">
                    <div style="margin-right: 8px;">${i18n("order.fields.total")}</div>
                    ${paymentMethodDivs.join(" ")} 
                </div>
                <div>${toMoney(transaction.totalPaid, true)}</div>
            </div>`
        ).appendTo(this.$totals);

        if (transaction.refundedAmount != 0) {
            $(`<div class='totals-item'>
                <div style="display: flex; ">
                    <div style="margin-right: 8px;">${trans.payments.refunded}</div>
                </div>
                <div class="refunded">${toMoney(transaction.refundedAmount * -1, true)}</div>
            </div>`
            ).appendTo(this.$totals);
        }

        if (transaction.state === PaymentState.Confirmed) {
            $(
                '<div id="receipt-section"><button class="btn btn-success ml-2 dropdown-toggle" type="button" id="receiptbtn" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">' +
                    i18n("receipt.receipt") +
                    "</button>" +
                    '<div class="dropdown-menu" aria-labelledby="receiptbtn"><a class="dropdown-item" href="#" data-action="sendreceipt">' +
                    i18n("receipt.sendReceipt") +
                    '</a><a class="dropdown-item" href="#" data-action="printreceipt">' +
                    i18n("receipt.printReceipt") +
                    "</a></div></div>"
            ).appendTo(this.$footer!);
        }

        this.$footer!.find("#receipt-section > .dropdown-menu > .dropdown-item").click((e) => {
            e.preventDefault();
            switch ($(e.currentTarget).attr("data-action")) {
                case "sendreceipt":
                    this.openSendReceipt(transaction.locationLanguage);
                    this.close();
                    break;
                case "printreceipt":
                    this.showPrintOrderReceipt(transaction.locationLanguage);
                    this.close();
                    break;
            }
        });
    }

    private showPrintOrderReceipt(locationLanguage: Language | undefined) {
        const m = new PrintReceiptWindow(this.transactionId, ReceiptType.Payment, locationLanguage);
        m.show();
    }

    public openSendReceipt(locationLanguage: Language | undefined) {
        const m = new SendOrderReceiptWindow(this.transactionId, ReceiptType.Payment, locationLanguage);
        m.show();
    }

    private getVatGroupTitle(vatGroup: IVatGroupDto): string {
        return i18n("order.consumptionInclVat", { vat: this.getVatPercentValueAsString(vatGroup.vatValue) });
    }

    private getVatPercentValueAsString(vat: number): string {
        return (Math.round(vat * 100000) / 1000).toString();
    }

    protected onShow() {}

    protected onHide() {
        this.$element.remove();
    }
}
